// src/components/tariffs/TariffsSection.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Drawer,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { X, Sparkles, FileText, ChevronDown, Plus, History, Trash2 } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import TariffChatbot from "./TariffChatbot";
import TariffCalculator from "./TariffCalculator";
import { ChatSession } from "./ChatSessionList";
import { apiService } from "../../services/api";
import { getUser } from "../../utils/session";
import { WL_RED } from "../../utils/designTokens";
import { formatTimestampForCalculationHistory } from "../../utils/timestampUtils";
import { useUser, useAuth } from "@clerk/clerk-react";
import { useUserRole } from "../../hooks/useUserRole";

interface TariffCalculationHistory {
  id: number;
  user_id: string;
  product_name?: string;
  hts_code: string;
  country_of_origin: string;
  import_value: number;
  import_quantity: number;
  total_duty_amount: number;
  result: any;
  created_at: string;
}

interface ChatMessage {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

interface HtsRequest {
  id: number;
  request_id: string;
  product_name: string;
  part_number?: string | null;
  part_description?: string | null;
  country_of_origin?: string | null;
  submitted_by_name?: string | null;
  created_at?: string | null;
  status: string;
  importance?: string | null;
  additional_comments?: string | null;
}
interface HtsRequestsTabContentProps {
  htsRequests: HtsRequest[];
  htsSearch: string;
  htsListLoading: boolean;
  setHtsSearch: React.Dispatch<React.SetStateAction<string>>;
  setHtsDetailRow: React.Dispatch<React.SetStateAction<HtsRequest | null>>;
}
interface CalculationHistoryListProps {
  calculationHistory: TariffCalculationHistory[];
  historyLoading: boolean;
  selectedCalculation: TariffCalculationHistory | null;
  onCalculationClick: (c: TariffCalculationHistory) => void;
  onViewMore: () => void;
  isMobile: boolean;
  formatCalculationTitle: (c: TariffCalculationHistory) => string;
  formatTimestamp: (t: string, calc?: TariffCalculationHistory) => string;
}
interface SessionHistoryContentProps {
  isLoadingSessions: boolean;
  sessions: ChatSession[];
  selectedSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
}

const fmtMoney = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtRate = (v: number | null | undefined) => {
  if (v == null) return "—";
  return `${Number.parseFloat(v.toFixed(1))}%`;
};

const getHtsStatusStyle = (status: string): React.CSSProperties => {
  if (status === "Approved")     return { background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0" };
  if (status === "Under Review") return { background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE" };
  if (status === "Pending")      return { background: "#FEF9C3", color: "#A16207", border: "1px solid #FDE68A" };
  if (status === "Rejected")     return { background: "#FEE2E2", color: "#DC2626", border: "1px solid #FECACA" };
  return { background: "#F3F4F6", color: "#374151" };
};

const getHtsStatusIcon = (status: string) => {
  if (status === "Approved")     return "✓ ";
  if (status === "Under Review") return "• ";
  if (status === "Pending")      return "⏱ ";
  if (status === "Rejected")     return "✕ ";
  return "";
};

const HTS_TABLE_COLUMNS: React.CSSProperties[] = [
  { width: 130, minWidth: 110 },
  { flex: 1 },
  { width: 140, minWidth: 110 },
  { width: 150, minWidth: 120 },
  { width: 140, minWidth: 120 },
  { width: 140, minWidth: 120 },
  { width: 90, minWidth: 70, textAlign: "right" as const },
];

const HTS_TABLE_HEADERS = [
  "REQUEST ID",
  "PRODUCT NAME",
  "COUNTRY OF ORIGIN",
  "SUBMITTED BY",
  "SUBMISSION DATE",
  "STATUS",
  "ACTIONS",
];

const HtsRequestsTableRows: React.FC<{
  filteredRequests: HtsRequest[];
  setHtsDetailRow: React.Dispatch<React.SetStateAction<HtsRequest | null>>;
}> = ({ filteredRequests, setHtsDetailRow }) => (
  <>
    {filteredRequests.map((row, idx) => (
      <div
        key={row.id}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px 20px",
          borderBottom: idx < filteredRequests.length - 1 ? "1px solid #F3F4F6" : "none",
          background: "#fff",
          transition: "background 0.12s",
        }}
      >
        <div style={{ ...HTS_TABLE_COLUMNS[0], fontSize: "0.875rem", fontWeight: 600, color: "#EF2D3E", paddingRight: 16 }}>{row.request_id}</div>
        <div style={{ ...HTS_TABLE_COLUMNS[1], fontSize: "0.875rem", color: "#111111", paddingRight: 16 }}>{row.product_name}</div>
        <div style={{ ...HTS_TABLE_COLUMNS[2], fontSize: "0.875rem", color: "#374151", paddingRight: 16 }}>{row.country_of_origin || "—"}</div>
        <div style={{ ...HTS_TABLE_COLUMNS[3], fontSize: "0.875rem", color: "#374151", paddingRight: 16 }}>{row.submitted_by_name || "—"}</div>
        <div style={{ ...HTS_TABLE_COLUMNS[4], fontSize: "0.875rem", color: "#374151", paddingRight: 16 }}>
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
        </div>
        <div style={{ ...HTS_TABLE_COLUMNS[5], paddingRight: 16 }}>
          <span
            style={{
              ...getHtsStatusStyle(row.status),
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: 20,
              padding: "3px 10px",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
          >
            {getHtsStatusIcon(row.status)}{row.status}
          </span>
        </div>
        <div style={{ ...HTS_TABLE_COLUMNS[6] }}>
          <button
            onClick={() => setHtsDetailRow(row)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "0.875rem",
              color: "#374151",
              fontWeight: 500,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#EF2D3E"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#374151"; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View
          </button>
        </div>
      </div>
    ))}
  </>
);

const HtsRequestsTableContent: React.FC<{
  htsListLoading: boolean;
  filteredRequests: HtsRequest[];
  totalRequests: number;
  setHtsDetailRow: React.Dispatch<React.SetStateAction<HtsRequest | null>>;
}> = ({ htsListLoading, filteredRequests, totalRequests, setHtsDetailRow }) => {
  if (htsListLoading) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center" }}>
        <div
          style={{
            display: "inline-block",
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: "2px solid #EF2D3E",
            borderTopColor: "transparent",
            animation: "spin 0.7s linear infinite",
          }}
        />
      </div>
    );
  }

  if (filteredRequests.length === 0) {
    const emptyMessage = totalRequests === 0 ? "No requests yet." : "No requests match your search.";
    return (
      <div style={{ padding: "40px 20px", textAlign: "center", color: "#6B7280", fontSize: "0.875rem" }}>
        {emptyMessage}
      </div>
    );
  }

  return <HtsRequestsTableRows filteredRequests={filteredRequests} setHtsDetailRow={setHtsDetailRow} />;
};

const HtsRequestsTabContent: React.FC<HtsRequestsTabContentProps> = ({
  htsRequests,
  htsSearch,
  htsListLoading,
  setHtsSearch,
  setHtsDetailRow,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSubmittedBy, setFilterSubmittedBy] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const normalizedSearch = htsSearch.toLowerCase();
  const filteredRequests = htsRequests.filter((request) => {
    const matchesSearch =
      (request.request_id ?? "").toLowerCase().includes(normalizedSearch) ||
      (request.product_name ?? "").toLowerCase().includes(normalizedSearch);
    if (!matchesSearch) return false;
    if (filterStatus && request.status !== filterStatus) return false;
    if (filterSubmittedBy &&
      !(request.submitted_by_name ?? "").toLowerCase().includes(filterSubmittedBy.toLowerCase()))
      return false;
    if (filterDateFrom && request.created_at &&
      new Date(request.created_at) < new Date(filterDateFrom)) return false;
    if (filterDateTo && request.created_at &&
      new Date(request.created_at) > new Date(filterDateTo + "T23:59:59")) return false;
    return true;
  });

  const activeFilterCount = [filterStatus, filterSubmittedBy, filterDateFrom, filterDateTo].filter(Boolean).length;

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
          >
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search by request ID or product name..."
            value={htsSearch}
            onChange={(e) => setHtsSearch(e.target.value)}
            style={{ width: "100%", height: 40, borderRadius: 8, border: "1px solid #E5E7EB",
              padding: "0 14px 0 38px", fontSize: "0.875rem", color: "#111111", outline: "none",
              boxSizing: "border-box" }}
          />
        </div>
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          style={{ display: "flex", alignItems: "center", gap: 6, height: 40,
            border: `1px solid ${filtersOpen || activeFilterCount > 0 ? "#374151" : "#E5E7EB"}`,
            background: filtersOpen || activeFilterCount > 0 ? "#F9FAFB" : "#fff",
            borderRadius: 8, padding: "0 16px", fontSize: "0.875rem", fontWeight: 500, color: "#374151", cursor: "pointer" }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span style={{ marginLeft: 4, background: "#374151", color: "#fff",
              borderRadius: 10, fontSize: "0.6875rem", fontWeight: 700, padding: "1px 6px" }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
      {filtersOpen && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap",
          padding: "14px 20px", borderBottom: "1px solid #E5E7EB", background: "#F9FAFB" }}>
          <div>
            <label htmlFor="hts-status-filter" style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#374151", marginBottom: 4 }}>Status</label>
            <select id="hts-status-filter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 10px",
                fontSize: "0.875rem", color: "#374151", background: "#fff", cursor: "pointer" }}>
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Under Review">Under Review</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label htmlFor="hts-submitted-by-filter" style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#374151", marginBottom: 4 }}>Submitted By</label>
            <input id="hts-submitted-by-filter" type="text" value={filterSubmittedBy} onChange={(e) => setFilterSubmittedBy(e.target.value)}
              placeholder="Filter by name..."
              style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 10px",
                fontSize: "0.875rem", color: "#374151", outline: "none", width: 180, boxSizing: "border-box" }} />
          </div>
          <div>
            <label htmlFor="hts-date-from-filter" style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#374151", marginBottom: 4 }}>From</label>
            <input id="hts-date-from-filter" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
              style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 8px",
                fontSize: "0.875rem", color: "#374151", outline: "none" }} />
          </div>
          <div>
            <label htmlFor="hts-date-to-filter" style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#374151", marginBottom: 4 }}>To</label>
            <input id="hts-date-to-filter" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
              style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 8px",
                fontSize: "0.875rem", color: "#374151", outline: "none" }} />
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setFilterStatus(""); setFilterSubmittedBy(""); setFilterDateFrom(""); setFilterDateTo(""); }}
              style={{ height: 36, border: "none", background: "none", fontSize: "0.875rem",
                color: "#6B7280", cursor: "pointer", padding: "0 4px", textDecoration: "underline" }}>
              Clear filters
            </button>
          )}
        </div>
      )}
      <div style={{ padding: "10px 20px", borderBottom: "1px solid #E5E7EB" }}>
        <span style={{ fontSize: "0.8125rem", color: "#6B7280" }}>
          {htsListLoading ? "Loading…" : `Showing ${filteredRequests.length} of ${htsRequests.length} requests`}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", padding: "12px 20px", borderBottom: "1px solid #E5E7EB", background: "#fff" }}>
          {HTS_TABLE_HEADERS.map((header, index) => (
            <div key={header} style={{ ...HTS_TABLE_COLUMNS[index], fontSize: "0.75rem", fontWeight: 700, color: "#374151",
              letterSpacing: "0.04em", paddingRight: index < HTS_TABLE_HEADERS.length - 1 ? 16 : 0 }}>
              {header}
            </div>
          ))}
        </div>
        <HtsRequestsTableContent
          htsListLoading={htsListLoading}
          filteredRequests={filteredRequests}
          totalRequests={htsRequests.length}
          setHtsDetailRow={setHtsDetailRow}
        />
      </div>
    </div>
  );
};
const CalculationHistoryList: React.FC<CalculationHistoryListProps> = ({
  calculationHistory,
  historyLoading,
  onCalculationClick,
  onViewMore,
  formatTimestamp,
}) => {
  if (historyLoading && calculationHistory.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#EF2D3E] border-t-transparent" />
      </div>
    );
  }

  if (calculationHistory.length === 0) {
    return (
      <p className="leading-relaxed text-[0.9rem] text-[#4B5563]">
        No calculations yet. Use the calculator to get started.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {calculationHistory.map((calc) => {
        const rate = calc.result?.effective_duty_rate_percent ?? null;
        const baseCost = calc.import_value ?? 0;
        const totalLanded = calc.result?.landed_cost ?? (baseCost + (calc.total_duty_amount ?? 0));
        return (
          <div
            key={calc.id}
            role="button"
            tabIndex={0}
            onClick={() => onCalculationClick(calc)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCalculationClick(calc);
              }
            }}
            style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 18px", background: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)", cursor: "pointer", transition: "all 0.15s ease-in-out" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#EF2D3E";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E5E7EB";
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)";
            }}
          >
            {/* Card header: timestamp */}
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                {formatTimestamp(calc.created_at)}
              </span>
            </div>

            {/* Data rows */}
            {([
              { label: "HTS Code", value: calc.hts_code, bold: true, red: false },
              { label: "Origin", value: calc.country_of_origin, bold: true, red: false },
              { label: "Base Cost", value: fmtMoney(baseCost), bold: true, red: false },
              { label: "Tariff Rate", value: fmtRate(rate), bold: false, red: true },
            ] as { label: string; value: string; bold: boolean; red: boolean }[]).map((row) => (
              <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: "0.875rem", color: "#6B7280" }}>{row.label}</span>
                <span style={{
                  fontSize: "0.875rem",
                  fontWeight: row.bold ? 700 : 400,
                  color: row.red ? "#EF2D3E" : "#111111",
                }}>{row.value}</span>
              </div>
            ))}

            {/* Divider */}
            <div style={{ borderTop: "1px solid #E5E7EB", margin: "14px 0" }} />

            {/* Total Landed Cost */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111" }}>Total Landed Cost</span>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#EF2D3E" }}>{fmtMoney(totalLanded)}</span>
            </div>
          </div>
        );
      })}

      <button
        onClick={onViewMore}
        disabled={historyLoading}
        style={{
          width: "100%", height: 40, borderRadius: 8,
          border: "1.5px solid #E5E7EB", background: "#fff",
          fontSize: "0.875rem", fontWeight: 600, color: "#374151",
          cursor: historyLoading ? "not-allowed" : "pointer",
          opacity: historyLoading ? 0.6 : 1,
          transition: "border-color 0.15s, color 0.15s, background 0.15s",
        }}
        onMouseEnter={(e) => {
          if (!historyLoading) {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#EF2D3E";
            (e.currentTarget as HTMLButtonElement).style.color = "#EF2D3E";
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = "#E5E7EB";
          (e.currentTarget as HTMLButtonElement).style.color = "#374151";
        }}
      >
        {historyLoading ? "Loading..." : "View More"}
      </button>
    </div>
  );
};

const SessionHistoryContent: React.FC<SessionHistoryContentProps> = ({
  isLoadingSessions,
  sessions,
  selectedSessionId,
  onSessionSelect,
}) => {
  if (isLoadingSessions && sessions.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "2px solid #EF2D3E",
            borderTopColor: "transparent",
            animation: "spin 0.7s linear infinite",
          }}
        />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <p style={{ fontSize: "0.875rem", color: "#6B7280", textAlign: "center", marginTop: 24 }}>
        No previous conversations yet.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {sessions.map((session) => {
        const sessionId = session.id || session.session_id || "";
        if (!sessionId) return null;
        const title = session.title || session.first_question || session.last_message || "Untitled session";
        const time = session.last_message_time || session.updated_at || session.created_at || null;
        const isActive = sessionId === selectedSessionId;
        return (
          <button
            key={sessionId}
            onClick={() => onSessionSelect(sessionId)}
            style={{
              textAlign: "left", background: isActive ? "#FEF2F2" : "#F9FAFB",
              border: `1px solid ${isActive ? "#EF2D3E" : "#E5E7EB"}`,
              borderRadius: 8, padding: "10px 12px", cursor: "pointer",
              transition: "border-color 0.15s, background 0.15s",
            }}
            onMouseEnter={(e) => { if (!isActive) { (e.currentTarget as HTMLButtonElement).style.borderColor = "#EF2D3E"; (e.currentTarget as HTMLButtonElement).style.background = "#FEF2F2"; } }}
            onMouseLeave={(e) => { if (!isActive) { (e.currentTarget as HTMLButtonElement).style.borderColor = "#E5E7EB"; (e.currentTarget as HTMLButtonElement).style.background = "#F9FAFB"; } }}
          >
            <p style={{ margin: 0, fontSize: "0.8125rem", fontWeight: 600, color: "#111111",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
            >
              {title.length > 60 ? `${title.slice(0, 57)}...` : title}
            </p>
            {time && (
              <span style={{ fontSize: "0.75rem", color: "#6B7280", marginTop: 3, display: "block" }}>
                {new Date(time).toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const TariffsSection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: clerkUser } = useUser();
  const { isLoaded: clerkLoaded } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Chat FAB drawer
  const [chatOpen, setChatOpen] = useState<boolean>(() => {
    const urlTab = searchParams.get("tab");
    const stateTab = (location.state as any)?.tab;
    return urlTab === "assistant" || stateTab === "assistant";
  });
  const [chatMinimized, setChatMinimized] = useState(false);
  const [chatShowHistory, setChatShowHistory] = useState(false);

  // Calculation history drawer
  const [historyOpen, setHistoryOpen] = useState(false);

  const { isAdmin } = useUserRole();

  // HTS Code Request modal
  const [htsRequestOpen, setHtsRequestOpen] = useState(false);
  const [htsForm, setHtsForm] = useState({
    productName: "",
    partNumber: "",
    partDescription: "",
    countryOfOrigin: "",
    importance: "Medium",
    additionalComments: "",
  });
  const [htsSubmitting, setHtsSubmitting] = useState(false);
  const [htsSubmitted, setHtsSubmitted] = useState(false);
  const [htsSearch, setHtsSearch] = useState("");
  const [htsDetailRow, setHtsDetailRow] = useState<HtsRequest | null>(null);
  const [htsRequests, setHtsRequests] = useState<HtsRequest[]>([]);
  const [htsListLoading, setHtsListLoading] = useState(false);
  const [htsStatusLoadingId, setHtsStatusLoadingId] = useState<number | null>(null);
  const [htsStatusError, setHtsStatusError] = useState<string | null>(null);
  const htsDialogRef = useRef<HTMLDialogElement | null>(null);

  const handleHtsFormChange = (field: string, value: string) => {
    setHtsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleHtsClear = useCallback(() => {
    setHtsForm({ productName: "", partNumber: "", partDescription: "", countryOfOrigin: "", importance: "Medium", additionalComments: "" });
    setHtsSubmitted(false);
  }, []);

  const closeHtsModal = useCallback(() => {
    setHtsRequestOpen(false);
    handleHtsClear();
  }, [handleHtsClear]);

  const handleHtsSubmit = async () => {
    if (!htsForm.productName.trim()) return;
    setHtsSubmitting(true);
    try {
      await apiService.submitHtsRequest({
        product_name: htsForm.productName,
        part_number: htsForm.partNumber || undefined,
        part_description: htsForm.partDescription || undefined,
        country_of_origin: htsForm.countryOfOrigin || undefined,
        importance: htsForm.importance,
        additional_comments: htsForm.additionalComments || undefined,
        submitted_by_name: clerkUser?.fullName || clerkUser?.firstName || getUser()?.email || "",
      });
      setHtsSubmitted(true);
      apiService.getHtsRequests().then((res) => setHtsRequests(res.data || []));
      setTimeout(() => { setHtsRequestOpen(false); handleHtsClear(); }, 1500);
    } catch {
      // Submission failed — keep modal open so user can retry
    } finally {
      setHtsSubmitting(false);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    setHtsStatusError(null);
    setHtsStatusLoadingId(id);
    try {
      await apiService.updateHtsRequestStatus(id, status);
      setHtsRequests((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
      setHtsDetailRow((prev) => prev ? { ...prev, status } : prev);
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update HTS request status.";
      setHtsStatusError(message);
    } finally {
      setHtsStatusLoadingId(null);
    }
  };

  const submittedUser = clerkUser?.fullName || clerkUser?.firstName || getUser()?.email || "";

  const [activeInnerTab, setActiveInnerTab] = useState(0);

  useEffect(() => {
    if (activeInnerTab !== 1 || !clerkLoaded) return;
    setHtsListLoading(true);
    apiService.getHtsRequests()
      .then((res) => setHtsRequests(res.data || []))
      .catch(() => setHtsRequests([]))
      .finally(() => setHtsListLoading(false));
  }, [activeInnerTab, clerkLoaded]);

  // Session-based chat history state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [startNewSessionOnNextMessage, setStartNewSessionOnNextMessage] = useState(false);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [clearChatConfirmOpen, setClearChatConfirmOpen] = useState(false);
  const [isClearingChat, setIsClearingChat] = useState(false);

  const [calculationHistory, setCalculationHistory] = useState<TariffCalculationHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyLimit, setHistoryLimit] = useState<number>(5);
  const [selectedCalculation, setSelectedCalculation] = useState<TariffCalculationHistory | null>(null);

  // Clean up legacy ?tab= URL param on mount
  useEffect(() => {
    if (searchParams.get("tab")) {
      const params = new URLSearchParams(searchParams);
      params.delete("tab");
      const queryString = params.toString();
      const nextPath = queryString ? `/tariffs?${queryString}` : "/tariffs";
      navigate(nextPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!htsRequestOpen) return undefined;

    const dialog = htsDialogRef.current;
    if (dialog && !dialog.open) {
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    }

    return () => {
      if (dialog?.open && typeof dialog.close === "function") {
        dialog.close();
      } else if (dialog?.hasAttribute("open")) {
        dialog.removeAttribute("open");
      }
    };
  }, [htsRequestOpen]);

  // Fetch chat sessions when the chat panel opens
  const fetchSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const response = await apiService.getChatSessions(10);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Failed to load chat sessions:", error);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (chatOpen) fetchSessions();
  }, [chatOpen, fetchSessions]);

  // Fetch messages for selected session
  const fetchSessionMessages = useCallback(async (sessionId: string) => {
    try {
      const response = await apiService.getChatMessages(sessionId);
      setChatMessages(response.data.messages || []);
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || "Failed to load messages";
      console.error(msg);
      setChatMessages([]);
    }
  }, []);

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setStartNewSessionOnNextMessage(false);
    fetchSessionMessages(sessionId);
    if (isMobile) {
      setChatOpen(false); // Close drawer on mobile after selection
    }
  };

  const handleNewTabFromHeader = () => {
    setStartNewSessionOnNextMessage(true);
    setSelectedSessionId(null);
    setChatMessages([]);
    setChatShowHistory(false);
  };

  const handleCloseAssistant = () => {
    setChatOpen(false);
    setChatShowHistory(false);
    setSelectedSessionId(null);
    setChatMessages([]);
    setStartNewSessionOnNextMessage(true);
    setChatMinimized(false);
  };

  // NEW: Delete handler (called from ChatSessionList)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await apiService.deleteChatSession(sessionId);

        // If the deleted session is currently selected, clear selection and messages
        if (selectedSessionId === sessionId) {
          setSelectedSessionId(null);
          setChatMessages([]);
          setStartNewSessionOnNextMessage(false);
        }

        // Refresh list after deletion
        await fetchSessions();
      } catch (error: any) {
        const msg = error.response?.data?.error || error.message || "Failed to delete session";
        console.error(msg);
        throw error; // lets the dialog keep open + allow retry
      }
    },
    [fetchSessions, selectedSessionId]
  );

  const handleClearCurrentChat = useCallback(async () => {
    if (!selectedSessionId) return;
    setIsClearingChat(true);
    try {
      await apiService.deleteChatSession(selectedSessionId);
      setSelectedSessionId(null);
      setChatMessages([]);
      setStartNewSessionOnNextMessage(false);
      await fetchSessions();
      setClearChatConfirmOpen(false);
    } catch (error: any) {
      console.error("Failed to clear chat:", error.message);
    } finally {
      setIsClearingChat(false);
    }
  }, [selectedSessionId, fetchSessions]);

  const selectedSession =
    sessions.find((s) => (s.id || s.session_id) === selectedSessionId) || null;
  const activeSessionTitle =
    selectedSession?.title ||
    selectedSession?.first_question ||
    selectedSession?.last_message ||
    null;

  const fetchCalculationHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const userId = clerkUser?.id || getUser()?.email || "anonymous";
      const response = await apiService.getTariffCalculationsHistory(userId, historyLimit);
      setCalculationHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching calculation history:", error);
      setCalculationHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [clerkUser?.id, historyLimit]);

  useEffect(() => {
    fetchCalculationHistory();
  }, [fetchCalculationHistory]);

  const formatCalculationTitle = (calc: TariffCalculationHistory): string => {
    const dutyAmount =
      typeof calc.total_duty_amount === "number"
        ? calc.total_duty_amount.toFixed(2)
        : Number.parseFloat(calc.total_duty_amount as any)?.toFixed(2) || "0.00";
    if (calc.product_name) {
      return `${calc.product_name} (${calc.country_of_origin}) - $${dutyAmount}`;
    }
    return `HTS ${calc.hts_code} from ${calc.country_of_origin} - $${dutyAmount}`;
  };

  const formatTimestamp = useCallback((timestamp: string): string => {
    return formatTimestampForCalculationHistory(timestamp);
  }, []);

  const handleCalculationClick = (calc: TariffCalculationHistory) => {
    setSelectedCalculation(calc);
    setHistoryOpen(false);
  };

  const handleNewCalculation = () => {
    setSelectedCalculation(null);
  };

  const handleViewMore = () => {
    setHistoryLimit((prev) => prev + 5);
  };

  return (
    <div style={{ minHeight: "100%", background: "#F7F7F7", padding: "32px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "1.5rem", fontWeight: 600, color: "#111111", letterSpacing: "-0.3px", fontFamily: "'Inter', sans-serif", lineHeight: 1 }}>
              {activeInnerTab === 1 ? "HTS Code Requests" : "Tariff Calculator"}
            </span>
            <Tooltip
              title={activeInnerTab === 1 ? "Review and manage requests submitted for HTS code classification" : "Calculate import duties and landed costs using the current U.S. Harmonized Tariff Schedule"}
              arrow
              placement="right"
            >
              <span style={{ cursor: "pointer", color: "#6B7280", lineHeight: 1, display: "flex", alignItems: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </Tooltip>
          </div>
          <p style={{ marginTop: 4, fontSize: "0.875rem", color: "#6B7280", margin: "4px 0 0 0" }}>
            {activeInnerTab === 1
              ? "Review and manage requests submitted for HTS code classification."
              : "Estimate import duties and landed cost using current tariff schedules"}
          </p>
        </div>
        {/* View History button */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {activeInnerTab === 0 && (
            <button
              onClick={() => setHistoryOpen(true)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F9FAFB"; (e.currentTarget as HTMLButtonElement).style.borderColor = "#9CA3AF"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; (e.currentTarget as HTMLButtonElement).style.borderColor = "#EAEAEA"; }}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                border: "1px solid #EAEAEA", background: "#fff", padding: "8px 16px",
                fontSize: "0.875rem", fontWeight: 500, color: "#374151",
                cursor: "pointer", borderRadius: 8, transition: "all 0.15s",
              }}
            >
              <FileText size={15} />
              <span>View History</span>
            </button>
          )}
          <button
            style={{
              display: "flex", alignItems: "center", gap: 6,
              border: "none", background: "#EF2D3E", padding: "8px 16px",
              fontSize: "0.875rem", fontWeight: 600, color: "#fff",
              cursor: "pointer", borderRadius: 8, transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#d42635"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#EF2D3E"; }}
            onClick={() => setHtsRequestOpen(true)}
          >
            <Plus size={15} />
            <span>Request HTS Code</span>
          </button>
        </div>
      </div>

      {/* Inner tabs */}
      <div style={{ borderBottom: "1px solid #EAEAEA", marginBottom: 20 }}>
        <Tabs
          value={activeInnerTab}
          onChange={(_, v) => setActiveInnerTab(v)}
          variant="standard"
          sx={{
            minHeight: 44,
            "& .MuiTabs-indicator": { backgroundColor: WL_RED, height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          <Tab
            label="Tariff Calculator"
            sx={{
              fontSize: "0.875rem", fontWeight: 500, textTransform: "none",
              minHeight: 44, py: 1, color: "#6B7280",
              "&.Mui-selected": { color: WL_RED, fontWeight: 600 },
            }}
          />
          <Tab
            label="HTS Code Request"
            sx={{
              fontSize: "0.875rem", fontWeight: 500, textTransform: "none",
              minHeight: 44, py: 1, color: "#6B7280",
              "&.Mui-selected": { color: WL_RED, fontWeight: 600 },
            }}
          />
        </Tabs>
      </div>

      {/* Tab content */}
      <div>
        {activeInnerTab === 0 && clerkLoaded && (
          <TariffCalculator
            onCalculationComplete={fetchCalculationHistory}
            selectedCalculation={selectedCalculation}
            onNewCalculation={handleNewCalculation}
          />
        )}
        {activeInnerTab === 1 && (
          <HtsRequestsTabContent
            htsRequests={htsRequests}
            htsSearch={htsSearch}
            htsListLoading={htsListLoading}
            setHtsSearch={setHtsSearch}
            setHtsDetailRow={setHtsDetailRow}
          />
        )}
      </div>

      {/* HTS Code Request Modal */}
      {htsRequestOpen && (() => {
        const isDisabled = !htsForm.productName.trim() || htsSubmitting;
        let submitLabel: React.ReactNode;
        if (htsSubmitted) { submitLabel = "✓ Submitted!"; }
        else if (htsSubmitting) { submitLabel = "Submitting..."; }
        else { submitLabel = <><FileText size={14} /> Submit Request</>; }
        return (
          <dialog
            ref={htsDialogRef}
            aria-label="Submit HTS Code Request"
            onCancel={(event) => {
              event.preventDefault();
              closeHtsModal();
            }}
            style={{ padding: 0, border: "none", background: "transparent", width: "min(760px, calc(100vw - 48px))",
              maxWidth: "none", maxHeight: "90vh", boxShadow: "none", overflow: "visible", zIndex: 2000, margin: "auto" }}
          >
            <button
              type="button"
              aria-label="Close backdrop"
              onClick={closeHtsModal}
              style={{ position: "fixed", inset: 0, border: "none", background: "rgba(0,0,0,0.35)", padding: 0, cursor: "pointer" }}
            />
            <div
              style={{ position: "relative", background: "#fff", borderRadius: 12, width: "100%",
                maxHeight: "90vh", display: "flex", flexDirection: "column",
                boxShadow: "0 8px 40px rgba(0,0,0,0.18)", zIndex: 1 }}
            >
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "24px 28px 20px", borderBottom: "1px solid #EAEAEA", flexShrink: 0 }}
              >
                <span style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#111111" }}>Submit HTS Code Request</span>
                <button onClick={closeHtsModal}
                  aria-label="Close modal"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", padding: 4, borderRadius: 6 }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Scrollable body — footer buttons scroll with content */}
              <div style={{ overflowY: "auto", padding: "24px 28px 28px", flex: 1 }}>
                {/* Product Name */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-productName" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                    Product Name <span style={{ color: "#EF2D3E" }}>*</span>
                  </label>
                  <input
                    id="hts-productName" type="text" placeholder="Enter product name"
                    value={htsForm.productName}
                    onChange={(e) => handleHtsFormChange("productName", e.target.value)}
                    style={{ width: "100%", height: 44, borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "0 14px", fontSize: "0.875rem", color: "#111111", outline: "none", boxSizing: "border-box" }}
                  />
                </div>

                {/* Part Number */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-partNumber" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>Part Number</label>
                  <input
                    id="hts-partNumber" type="text" placeholder="Enter part number (optional)"
                    value={htsForm.partNumber}
                    onChange={(e) => handleHtsFormChange("partNumber", e.target.value)}
                    style={{ width: "100%", height: 44, borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "0 14px", fontSize: "0.875rem", color: "#111111", outline: "none", boxSizing: "border-box" }}
                  />
                </div>

                {/* Part Description */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-partDescription" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>Part Description</label>
                  <textarea
                    id="hts-partDescription" placeholder="Optional product description"
                    value={htsForm.partDescription}
                    onChange={(e) => handleHtsFormChange("partDescription", e.target.value)}
                    rows={4}
                    style={{ width: "100%", borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "12px 14px", fontSize: "0.875rem", color: "#111111", outline: "none",
                      resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                </div>

                {/* Submitted User */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-submittedUser" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                    Submitted User <span style={{ color: "#EF2D3E" }}>*</span>
                  </label>
                  <input
                    id="hts-submittedUser" type="text" readOnly value={submittedUser}
                    style={{ width: "100%", height: 44, borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "0 14px", fontSize: "0.875rem", color: "#111111", background: "#F9FAFB",
                      outline: "none", boxSizing: "border-box", cursor: "default" }}
                  />
                </div>

                {/* Country of Origin */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-countryOfOrigin" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>Country of Origin</label>
                  <input
                    id="hts-countryOfOrigin" type="text" placeholder="e.g. China, Germany, Mexico (optional)"
                    value={htsForm.countryOfOrigin}
                    onChange={(e) => handleHtsFormChange("countryOfOrigin", e.target.value)}
                    style={{ width: "100%", height: 44, borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "0 14px", fontSize: "0.875rem", color: "#111111", outline: "none", boxSizing: "border-box" }}
                  />
                </div>

                {/* Importance */}
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="hts-importance" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>Importance</label>
                  <div style={{ position: "relative" }}>
                    <select
                      id="hts-importance"
                      value={htsForm.importance}
                      onChange={(e) => handleHtsFormChange("importance", e.target.value)}
                      style={{ width: "100%", height: 44, borderRadius: 8, border: "1px solid #E5E7EB",
                        padding: "0 40px 0 14px", fontSize: "0.875rem", color: "#111111",
                        background: "#fff", outline: "none", appearance: "none", boxSizing: "border-box", cursor: "pointer" }}
                    >
                      {["Low", "Medium", "High", "Critical"].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "#6B7280", pointerEvents: "none" }} />
                  </div>
                </div>

                {/* Additional Comments */}
                <div style={{ marginBottom: 28 }}>
                  <label htmlFor="hts-comments" style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>Additional Comments</label>
                  <textarea
                    id="hts-comments"
                    placeholder="Add any additional information about this request..."
                    value={htsForm.additionalComments}
                    onChange={(e) => handleHtsFormChange("additionalComments", e.target.value)}
                    rows={4}
                    style={{ width: "100%", borderRadius: 8, border: "1px solid #E5E7EB",
                      padding: "12px 14px", fontSize: "0.875rem", color: "#111111", outline: "none",
                      resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                </div>

                {/* Action buttons — scroll with form */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <button
                    onClick={handleHtsClear}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "none",
                      border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 18px",
                      fontSize: "0.875rem", fontWeight: 500, color: "#374151", cursor: "pointer" }}
                  >
                    <X size={14} /> Clear
                  </button>
                  <button
                    onClick={handleHtsSubmit}
                    disabled={isDisabled}
                    style={{ display: "flex", alignItems: "center", gap: 6,
                      background: isDisabled ? "#9CA3AF" : "#EF2D3E",
                      border: "none", borderRadius: 8, padding: "10px 22px",
                      fontSize: "0.875rem", fontWeight: 600, color: "#fff",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.7 : 1,
                      transition: "background 0.15s" }}
                  >
                    {submitLabel}
                  </button>
                </div>
              </div>
            </div>
          </dialog>
        );
      })()}

      {/* Floating AI Chat button - hidden when chat is open */}
      {!chatOpen && (
        <button
          onClick={() => { setChatOpen(true); setChatMinimized(false); }}
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 1300,
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 56, height: 56, borderRadius: "50%",
            background: WL_RED, border: "none", cursor: "pointer",
            boxShadow: "0 4px 20px rgba(239,45,62,0.45)",
          }}
          aria-label="Open AI Assistant"
        >
          <Sparkles size={22} style={{ color: "#fff" }} />
        </button>
      )}

      {/* AI Chat Popup */}
      {chatOpen && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1400,
            width: 440,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 12,
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: chatMinimized ? "auto" : 560,
            maxHeight: "calc(100vh - 48px)",
            transition: "height 0.2s ease",
          }}
        >
          {/* Popup header */}
          <div style={{ display: "flex", flexShrink: 0, alignItems: "center", justifyContent: "space-between", background: "#EF2D3E", padding: "14px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkles size={18} color="#fff" />
              <p style={{ fontWeight: 600, color: "#fff", margin: 0, fontSize: "0.9375rem" }}>WorldLink Assistant</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={handleNewTabFromHeader}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                style={{ background: "transparent", border: "none", cursor: "pointer", borderRadius: 6, padding: 4, display: "flex", alignItems: "center" }}
                aria-label="New tab"
                title="New Tab"
              >
                <Plus size={18} color="#fff" />
              </button>
              {/* History icon */}
              <button
                onClick={() => { setChatShowHistory((v) => !v); setChatMinimized(false); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = chatShowHistory ? "rgba(255,255,255,0.15)" : "transparent"; }}
                style={{ background: chatShowHistory ? "rgba(255,255,255,0.15)" : "transparent", border: "none", cursor: "pointer", borderRadius: 6, padding: 4, display: "flex", alignItems: "center" }}
                aria-label="Chat history"
              >
                <History size={18} color="#fff" />
              </button>
              {/* Clear current chat — only when a session is active and chat panel is open */}
              {selectedSessionId && !chatShowHistory && (
                <button
                  onClick={() => setClearChatConfirmOpen(true)}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.2)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  style={{ background: "transparent", border: "none", cursor: "pointer", borderRadius: 6, padding: 4, display: "flex", alignItems: "center" }}
                  aria-label="Clear current chat"
                  title="Clear current chat"
                >
                  <Trash2 size={18} color="#fff" />
                </button>
              )}
              <button
                onClick={() => { setChatMinimized((m) => !m); if (chatMinimized) setChatShowHistory(false); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                style={{ background: "transparent", border: "none", cursor: "pointer", borderRadius: 6, padding: 4, display: "flex", alignItems: "center" }}
                aria-label={chatMinimized ? "Expand" : "Minimize"}
              >
                <ChevronDown size={18} color="#fff" style={{ transform: chatMinimized ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </button>
              <button
                onClick={handleCloseAssistant}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                style={{ background: "transparent", border: "none", cursor: "pointer", borderRadius: 6, padding: 4, display: "flex", alignItems: "center" }}
              >
                <X size={18} color="#fff" />
              </button>
            </div>
          </div>

          {/* Chat body */}
          {!chatMinimized && (
            <div style={{ display: "flex", flex: 1, minHeight: 0, flexDirection: "column", overflow: "hidden" }}>
              {chatShowHistory ? (
                /* ── Session history panel ── */
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                  {/* Panel header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 16px", borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}
                  >
                    <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "#111111" }}>Chat History</span>
                  </div>
                  {/* Session list */}
                  <div style={{ overflowY: "auto", flex: 1, padding: "12px 12px" }}>
                    <SessionHistoryContent
                      isLoadingSessions={isLoadingSessions}
                      sessions={sessions}
                      selectedSessionId={selectedSessionId}
                      onSessionSelect={(sessionId) => {
                        handleSelectSession(sessionId);
                        setChatShowHistory(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
                /* ── Active chat ── */
                <TariffChatbot
                  onSessionCreated={(sessionId) => {
                    setSelectedSessionId(sessionId);
                    fetchSessions();
                  }}
                  onSessionsUpdated={fetchSessions}
                  currentSessionId={selectedSessionId}
                  startNewSession={startNewSessionOnNextMessage}
                  onNewSessionStarted={() => setStartNewSessionOnNextMessage(false)}
                  historicalMessages={chatMessages}
                  activeSessionTitle={activeSessionTitle}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Clear current chat confirmation dialog */}
      <Dialog open={clearChatConfirmOpen} onClose={() => { if (!isClearingChat) setClearChatConfirmOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#111111" }}>Clear current chat?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6B7280" }}>
            This will permanently delete this conversation and all its messages.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <button
            onClick={() => setClearChatConfirmOpen(false)}
            disabled={isClearingChat}
            style={{ background: "#fff", border: "1px solid #EAEAEA", borderRadius: 8, padding: "8px 16px",
              fontSize: "0.875rem", fontWeight: 600, color: "#374151", cursor: isClearingChat ? "not-allowed" : "pointer", opacity: isClearingChat ? 0.5 : 1 }}
          >
            Cancel
          </button>
          <button
            onClick={handleClearCurrentChat}
            disabled={isClearingChat}
            style={{ background: "#EF2D3E", border: "none", borderRadius: 8, padding: "8px 16px",
              fontSize: "0.875rem", fontWeight: 600, color: "#fff", cursor: isClearingChat ? "not-allowed" : "pointer", opacity: isClearingChat ? 0.6 : 1 }}
          >
            {isClearingChat ? "Clearing..." : "Clear Chat"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Request Details Drawer */}
      <Drawer
        anchor="right"
        open={!!htsDetailRow}
        onClose={() => setHtsDetailRow(null)}
        slotProps={{
          paper: {
            sx: { width: isMobile ? "92%" : 460, maxWidth: "100vw", bgcolor: "#fff", display: "flex", flexDirection: "column" },
          },
        }}
      >
        {htsDetailRow && (() => {
          const row = htsDetailRow;
          const statusStyle: React.CSSProperties = getHtsStatusStyle(row.status);
          const statusDot: React.CSSProperties = { width: 7, height: 7, borderRadius: "50%", background: "currentColor", display: "inline-block", marginRight: 5 };
          const btnFont = "'Inter', sans-serif";
          return (
            <>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "20px 24px 18px", borderBottom: "1px solid #E5E7EB", flexShrink: 0,
                fontFamily: "'Inter', sans-serif" }}>
                <span style={{ fontSize: "1rem", fontWeight: 700, color: "#111111" }}>Request Details</span>
                <button
                  onClick={() => setHtsDetailRow(null)}
                  aria-label="Close details"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280",
                    display: "flex", alignItems: "center", padding: 4, borderRadius: 6 }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#EF2D3E"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#6B7280"; }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Scrollable body */}
              <div style={{ overflowY: "auto", flex: 1, padding: "24px 24px 32px", fontFamily: "'Inter', sans-serif" }}>

                {/* Product name + status badge */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: "1.125rem", fontWeight: 700, color: "#111111", lineHeight: 1.3, fontFamily: "'Inter', sans-serif" }}>{row.product_name}</span>
                  <span style={{ ...statusStyle, fontSize: "0.75rem", fontWeight: 600,
                    borderRadius: 20, padding: "4px 11px", whiteSpace: "nowrap", flexShrink: 0,
                    display: "inline-flex", alignItems: "center" }}>
                    <span style={statusDot} />
                    {row.status}
                  </span>
                </div>
                <p style={{ fontSize: "0.8125rem", color: "#6B7280", margin: "0 0 24px" }}>{row.request_id}</p>

                {/* Product Description */}
                {row.part_description && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 6px" }}>Product Description</p>
                    <p style={{ fontSize: "0.875rem", color: "#374151", margin: 0, lineHeight: 1.65 }}>{row.part_description}</p>
                  </div>
                )}

                {/* Part Number */}
                {row.part_number && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 6px" }}>Part Number</p>
                    <p style={{ fontSize: "0.875rem", color: "#374151", margin: 0 }}>{row.part_number}</p>
                  </div>
                )}

                {/* Country of Origin + Submission Date */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid #F3F4F6" }}>
                  <div>
                    <p style={{ fontSize: "0.75rem", color: "#64748B", margin: "0 0 4px", fontWeight: 500 }}>Country of Origin</p>
                    <p style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#111111", margin: 0 }}>{row.country_of_origin || "—"}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "0.75rem", color: "#64748B", margin: "0 0 4px", fontWeight: 500 }}>Submission Date</p>
                    <p style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#111111", margin: 0 }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                </div>

                {/* Submitted By + Importance */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  <div>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 6px" }}>Submitted By</p>
                    <p style={{ fontSize: "0.875rem", color: "#374151", margin: 0 }}>{row.submitted_by_name || "—"}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 6px" }}>Importance</p>
                    <p style={{ fontSize: "0.875rem", color: "#374151", margin: 0 }}>{row.importance || "—"}</p>
                  </div>
                </div>

                {/* Additional Notes */}
                {row.additional_comments && (
                  <div style={{ marginBottom: 28 }}>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 6px" }}>Additional Notes</p>
                    <p style={{ fontSize: "0.875rem", color: "#374151", margin: 0, lineHeight: 1.65 }}>{row.additional_comments}</p>
                  </div>
                )}

                {/* Admin Actions — only visible to admin / super_admin */}
                {isAdmin && (
                  <div>
                    <p style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", margin: "0 0 10px" }}>Update Status</p>
                    {htsStatusError && (
                      <p style={{ fontSize: "0.8125rem", color: "#B91C1C", margin: "0 0 10px" }}>
                        {htsStatusError}
                      </p>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {(["Under Review", "Approved", "Rejected", "Pending"] as const).map((s) => {
                        const isActive = row.status === s;
                        const isUpdating = htsStatusLoadingId === row.id;
                        const colors: Record<string, { bg: string; hover: string }> = {
                          "Under Review": { bg: "#2563EB", hover: "#1d4ed8" },
                          "Approved":     { bg: "#16A34A", hover: "#15803d" },
                          "Rejected":     { bg: "#EF2D3E", hover: "#d42635" },
                          "Pending":      { bg: "#A16207", hover: "#92400e" },
                        };
                        const { bg, hover } = colors[s];
                        let buttonLabel = `Mark as ${s}`;
                        if (isUpdating) buttonLabel = "Updating...";
                        else if (isActive) buttonLabel = `✓ ${s}`;
                        return (
                          <button
                            key={s}
                            disabled={isActive || isUpdating}
                            onClick={() => handleStatusChange(row.id, s)}
                            style={{ width: "100%", height: 44, borderRadius: 10, border: "none",
                              background: (isActive || isUpdating) ? "#E5E7EB" : bg,
                              fontSize: "0.875rem", fontWeight: 600,
                              color: (isActive || isUpdating) ? "#9CA3AF" : "#fff",
                              opacity: (isActive || isUpdating) ? 0.55 : 0.7,
                              cursor: (isActive || isUpdating) ? "default" : "pointer",
                              fontFamily: btnFont, transition: "background 0.15s" }}
                            onMouseEnter={(e) => { if (!isActive && !isUpdating) (e.currentTarget as HTMLButtonElement).style.background = hover; }}
                            onMouseLeave={(e) => { if (!isActive && !isUpdating) (e.currentTarget as HTMLButtonElement).style.background = bg; }}
                          >
                            {buttonLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </Drawer>

      {/* Calculation History Drawer */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        slotProps={{
          paper: {
            sx: { width: isMobile ? "92%" : 380, maxWidth: "100vw", bgcolor: "#F7F8FA" },
          },
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 20px 16px", borderBottom: "1px solid #E5E7EB",
          background: "#fff", flexShrink: 0 }}
        >
          <span style={{ fontSize: "1rem", fontWeight: 700, color: "#111111" }}>Calculator History</span>
          <button
            onClick={() => setHistoryOpen(false)}
            aria-label="Close history"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280",
              display: "flex", alignItems: "center", padding: 4, borderRadius: 6 }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#EF2D3E"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#6B7280"; }}
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 16px 24px", flex: 1 }}>
          <CalculationHistoryList
            calculationHistory={calculationHistory}
            historyLoading={historyLoading}
            selectedCalculation={selectedCalculation}
            onCalculationClick={handleCalculationClick}
            onViewMore={handleViewMore}
            isMobile={true}
            formatCalculationTitle={formatCalculationTitle}
            formatTimestamp={formatTimestamp}
          />
        </div>
      </Drawer>
    </div>
  );
};

export default TariffsSection;

